"use client";

import {MainNavItem} from "@/configs/nav.config";
import Link from "next/link";
import {ComponentProps} from "react";
import {cn} from "@/lib/utils";
import {
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarTrigger,
    useSidebar,
} from "@/components/ui/sidebar";
import {Button} from "@/components/ui/button";
import Logo from "@/components/icons/logo";
import {usePathname} from "next/navigation";
import {LogIn, LayoutDashboard} from "lucide-react";

const currentYear = new Date().getFullYear();

export interface NavMobileProps extends ComponentProps<'div'> {
    navItems: MainNavItem[];
    isLoggedIn?: boolean;
}

export default function NavMobile({className, navItems, isLoggedIn = false, ...props}: NavMobileProps) {

    const pathname = usePathname();
    const sidebar = useSidebar();

    function close() {
        sidebar.setOpenMobile(false);
        sidebar.setOpen(false);
    }

    if (!sidebar.isMobile) return null;

    const isActive = (href?: string) =>
        !!href &&
        (href === "/"
            ? pathname === "/"
            : pathname === href || pathname.startsWith(href + "/"));

    return (
        <>
            <SidebarHeader>
                <div className="flex items-center justify-between px-1">
                    <Logo width={32} height={32}/>
                    <SidebarTrigger/>
                </div>
            </SidebarHeader>

            <SidebarContent {...props} className={cn(className)}>
                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            {navItems.map((item) => {
                                // Simple top-level link (Contact, Blog, …).
                                if (item.kind === "link") {
                                    return (
                                        <SidebarMenuItem key={item.id}>
                                            <SidebarMenuButton asChild isActive={isActive(item.href)}>
                                                <Link href={item.href} onClick={close}>
                                                    {item.label}
                                                </Link>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    );
                                }

                                // Section with children (Home, Projects).
                                const linkHref = item.href ?? item.featured?.href;
                                // Skip in-page anchors (e.g. /#technologies) — those are
                                // sections of a page, not separate nav destinations.
                                const subs = item.items.filter((s) => !s.href.includes("#"));
                                return (
                                    <SidebarMenuItem key={item.id}>
                                        {linkHref ? (
                                            <SidebarMenuButton asChild isActive={isActive(linkHref)}>
                                                <Link href={linkHref} onClick={close}>
                                                    {item.label}
                                                </Link>
                                            </SidebarMenuButton>
                                        ) : (
                                            <SidebarMenuButton className="pointer-events-none font-medium text-sidebar-foreground/70">
                                                {item.label}
                                            </SidebarMenuButton>
                                        )}
                                        {subs.length > 0 && (
                                            <SidebarMenuSub>
                                                {subs.map((sub) => (
                                                    <SidebarMenuSubItem key={sub.href}>
                                                        <SidebarMenuSubButton
                                                            asChild
                                                            isActive={isActive(sub.href)}
                                                        >
                                                            <Link href={sub.href} onClick={close}>
                                                                {sub.title}
                                                            </Link>
                                                        </SidebarMenuSubButton>
                                                    </SidebarMenuSubItem>
                                                ))}
                                            </SidebarMenuSub>
                                        )}
                                    </SidebarMenuItem>
                                );
                            })}
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>

            <SidebarFooter className="gap-3">
                <Button variant="ghost" className="w-full" asChild>
                    <Link href={isLoggedIn ? "/admin" : "/admin/login"} onClick={close}>
                        {isLoggedIn ? (
                            <>
                                <LayoutDashboard/>
                                Dashboard
                            </>
                        ) : (
                            <>
                                <LogIn/>
                                Login
                            </>
                        )}
                    </Link>
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                    Josh Wood &copy; {currentYear}
                </p>
            </SidebarFooter>
        </>
    );
}
